import {
  ActionIcon, Badge, Box, Button, Group, Image, Modal, Paper, Stack, Table, Text, Title, Tooltip, UnstyledButton
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { CaretLeftIcon, CaretRightIcon, ImageIcon, PlusIcon } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DisabledReason, HelpButtonGroup, HelpIcon } from "@dehub/theme";
import type { IReturnDamagedInventory, IReturnItem } from "../../apis/return";
import { ReasonBadge, subReasonLabel } from "./atoms";
import { MixedResolutionPicker } from "./mixed-resolution-picker";
import RenderText from "../ui/render-text";
import { ReasonTooltip } from "../ui";
import { currencyFormat } from "../../utils/currency";
import { dateTimeSecFormat } from "../../utils/date";
import classes from "./items-table.module.css";
import { StatusPill } from "../ui/status-pill";

// Long unbreakable text → clipped to one line (ellipsis) inside its column,
// full value on hover. Box (not Text) so it doesn't trip the Table.Td lint.
function TruncCell ({ text, mono }: { text?: string | null; mono?: boolean }){
  if (!text) return <RenderText text={null} size="sm" color="dimmed" fallback="-" />;
  return (
    <Tooltip label={text} withArrow openDelay={400}>
      <Box className={classes.truncate} fz="sm" ff={mono ? "monospace" : undefined}>{text}</Box>
    </Tooltip>
  );
}

const TNUM = { fontVariantNumeric: "tabular-nums" } as const;

const fmtDate = (s?: string | null) => dateTimeSecFormat(s);

interface Props {
  items: IReturnItem[];
  currency: string;
  damagedInventory?: IReturnDamagedInventory[];
  onAllocate?: (item: IReturnItem) => void;
  canAllocate?: boolean;
  /** Why allocation is unavailable (permission RETURN_SET_INVENTORY_IMPACT); the button stays visible. */
  allocateReason?: string | null;
  /** @dehub/help reason code for `allocateReason` (PERM, NOT_IN_STATUS, …). */
  allocateCode?: string | null;
  // Phase H19 — mixed resolution picker
  returnRequestId?: string;
  parentResolutionType?: string | null;
  parentStatus?: string;
  onItemUpdated?: () => void;
}

// Small labelled metric (Quantity history cells).
function QtyStat ({ label, value, color }: { label: string; value: React.ReactNode; color?: string }){
  return (
    <Stack gap={0} align="flex-start">
      <Text size="sm" c="dimmed">{label}</Text>
      <Text size="sm" fw={600} c={color} style={TNUM}>{value}</Text>
    </Stack>
  );
}

export function ReturnItemsTable ({
  items,
  damagedInventory = [],
  onAllocate,
  canAllocate = false,
  allocateReason,
  allocateCode,
  returnRequestId,
  parentResolutionType,
  parentStatus,
  onItemUpdated
}: Props){
  const { t } = useTranslation("returns");
  // Allocated qty per ReturnItem id (avoids an inner reduce per row).
  const allocatedById = useMemo(() => {
    const map = new Map<string, number>();
    for (const d of damagedInventory) {
      map.set(d.returnItemId, (map.get(d.returnItemId) ?? 0) + d.quantity);
    }
    return map;
  }, [damagedInventory]);

  const showMixedPicker =
    !!returnRequestId && !!parentStatus && parentResolutionType === "MIXED";

  // ── Shared zoomable gallery (lifted: table thumbnails + gallery section) ──
  const [galleryOpened, galleryH] = useDisclosure(false);
  const [gallery, setGallery] = useState<string[]>([]);
  const [gIdx, setGIdx] = useState(0);
  const openGallery = (photos: string[], start = 0) => { setGallery(photos); setGIdx(start); galleryH.open(); };
  const gPrev = () => setGIdx((i) => (i - 1 + gallery.length) % gallery.length);
  const gNext = () => setGIdx((i) => (i + 1) % gallery.length);

  if (!items.length) return <Text c="dimmed">{t("itemsTable.noItems")}</Text>;

  // Aggregate evidence photos across items for the gallery section.
  const allPhotos = items.flatMap((it) => it.evidencePhotos ?? []);

  return (
    <Stack gap="lg">
      {/* ── 1. Items table (lean) ── */}
      <Stack gap="xs">
        <Title order={6}>
          {t("itemsTable.tableTitle", { count: items.length })}
          <HelpIcon helpKey="order.returns-detail.section.items" variant="section" />
        </Title>
        <Table withTableBorder withColumnBorders layout="fixed" horizontalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("itemsTable.col.product")}</Table.Th>
                <Table.Th w={96}>{t("itemsTable.col.sku")}</Table.Th>
                <Table.Th w={92}>{t("itemsTable.col.variant")}</Table.Th>
                <Table.Th w={48} ta="center">{t("itemsTable.col.qty")}</Table.Th>
                <Table.Th w={128} ta="right">{t("itemsTable.col.price")}</Table.Th>
                <Table.Th w={128} ta="right">{t("itemsTable.col.amount")}</Table.Th>
                <Table.Th w={140}>{t("itemsTable.col.reason")}</Table.Th>
                <Table.Th w={60}>{t("itemsTable.col.photo")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((it) => {
                const photos = it.evidencePhotos ?? [];
                return (
                  <Table.Tr key={it.id}>
                    <Table.Td><TruncCell text={it.productName} /></Table.Td>
                    <Table.Td><TruncCell text={it.sku} mono /></Table.Td>
                    <Table.Td><TruncCell text={it.variantName} /></Table.Td>
                    <Table.Td ta="center" style={TNUM}>{it.quantity}</Table.Td>
                    <Table.Td ta="right">
                      <RenderText isAmount text={currencyFormat(it.unitPrice)} size="sm" style={TNUM} />
                    </Table.Td>
                    <Table.Td ta="right">
                      <RenderText isAmount text={currencyFormat(it.lineTotal)} size="sm" style={TNUM} />
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2} align="flex-start">
                        <ReasonBadge reason={it.returnReason} />
                        {it.subReason ? <Box className={classes.wrap} fz="sm" c="dimmed">{t("listTable.subPrefix", { label: subReasonLabel(it.subReason) })}</Box> : null}
                        {it.subReasonNote ? <Box className={classes.wrap} fz="sm" c="dimmed">{it.subReasonNote}</Box> : null}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      {photos.length ? (
                        <UnstyledButton onClick={() => openGallery(photos, 0)} aria-label={t("itemsTable.viewPhoto")}>
                          <Group gap={4} wrap="nowrap" align="center">
                            <Image src={photos[0]} w={28} h={28} radius="sm" fit="cover" fallbackSrc="https://placehold.co/56?text=?" />
                            <RenderText text={`(${photos.length})`} size="sm" color="dimmed" />
                          </Group>
                        </UnstyledButton>
                      ) : (
                        <RenderText text={null} size="sm" color="dimmed" fallback="-" />
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
        </Table>
      </Stack>

      {/* ── 2. Photo evidence gallery ── */}
      <Paper withBorder radius="md" p="md">
        <Group justify="space-between" mb="sm">
          <Title order={6}>{t("itemsTable.photoEvidence")}</Title>
          <ReasonTooltip reason={t("itemsTable.addPhotoTooltip")}>
            <Button variant="default" leftSection={<PlusIcon size={16} />}>
              {t("itemsTable.addPhoto")}
            </Button>
          </ReasonTooltip>
        </Group>
        {allPhotos.length ? (
          <Group gap="md" wrap="wrap">
            {allPhotos.map((url, i) => (
              <Stack key={i} gap={2} align="center">
                <UnstyledButton onClick={() => openGallery(allPhotos, i)} aria-label={t("transitResolve.zoom")}>
                  <Image src={url} w={120} h={90} radius="md" fit="cover" fallbackSrc="https://placehold.co/240x180?text=No+image" />
                </UnstyledButton>
                <Text size="sm" c="dimmed">{t("transitResolve.zoom")}</Text>
              </Stack>
            ))}
          </Group>
        ) : (
          <Text size="sm" c="dimmed">{t("offSystem.noPhotos")}</Text>
        )}
      </Paper>

      {/* ── 3. Inventory impact (D10) ── */}
      <Paper withBorder radius="md" p="md">
        <Title order={6} mb="sm">
          {t("itemsTable.inventoryImpactTitle")}
          <HelpIcon helpKey="order.returns-detail.section.inventory-impact" variant="section" />
        </Title>
        <Stack gap="sm">
          {items.map((it) => {
            const allocated = allocatedById.get(it.id) ?? 0;
            const remaining = it.quantity - allocated;
            const isReceived = it.receivedQuantity != null || it.itemStatus === "RECEIVED";
            return (
              <Group key={it.id} justify="space-between" align="flex-start" wrap="wrap" gap="sm">
                <Stack gap={4} align="flex-start" flex={1}>
                  <Text size="sm" fw={500}>{it.productName}</Text>
                  <Group gap="xs" wrap="wrap" align="center">
                    <StatusPill tone={isReceived ? "green" : "gray"}>
                      {isReceived ? t("itemsTable.received") : t("itemsTable.pending")}
                    </StatusPill>
                    {it.inventoryImpact ? <Badge variant="light" color="gray" size="lg" radius="xl">{t(`inventoryImpact.bucket.${it.inventoryImpact}`, { defaultValue: "-" })}</Badge> : null}
                    {it.receivedCondition ? (
                      <Text size="sm" c="var(--nw-text-2)">
                        {t(`itemsTable.condition.${it.receivedCondition}`, { defaultValue: it.receivedCondition })}
                      </Text>
                    ) : null}
                    {it.bucketLocationCode ? <Text size="sm" c="dimmed">{t("itemsTable.bucketPrefix", { code: it.bucketLocationCode })}</Text> : null}
                    {it.receivedAt ? <Text size="sm" c="dimmed" style={TNUM}>{fmtDate(it.receivedAt)}</Text> : null}
                    {allocated > 0 ? <Text size="sm" c="dimmed" style={TNUM}>{t("itemsTable.allocated", { allocated, quantity: it.quantity })}</Text> : null}
                  </Group>
                  {!isReceived ? (
                    <Text size="sm" c="dimmed">{t("itemsTable.pendingHint")}</Text>
                  ) : null}
                </Stack>
                <Group gap="xs" wrap="nowrap">
                  {showMixedPicker ? (
                    <MixedResolutionPicker
                      returnRequestId={returnRequestId!}
                      returnItemId={it.id}
                      itemQuantity={it.quantity}
                      itemUnitPrice={it.unitPrice}
                      initial={{
                        itemResolutionType: it.itemResolutionType ?? null,
                        itemRefundMethod  : it.itemRefundMethod ?? null,
                        itemRefundAmount  : it.itemRefundAmount ?? null
                      }}
                      parentResolutionType={parentResolutionType ?? null}
                      parentStatus={parentStatus!}
                      onSaved={onItemUpdated ?? (() => undefined)}
                    />
                  ) : null}
                  {canAllocate ? (
                    // @dehub/help: attached «?» (R2); without the permission / status the reason popover (R4).
                    allocateReason ? (
                      <DisabledReason helpKey="order.returns-detail.reason.allocate" reason={allocateCode ?? null} when position="bottom-end">
                        <HelpButtonGroup helpKey="order.returns-detail.action.allocate">
                          <Button variant="default" onClick={() => onAllocate?.(it)}>
                            {it.inventoryImpact ? t("itemsTable.edit") : t("itemsTable.allocate")}
                          </Button>
                        </HelpButtonGroup>
                      </DisabledReason>
                    ) : (
                      <ReasonTooltip reason={remaining <= 0 ? t("inventoryImpact.allAllocatedTitle") : undefined}>
                        <HelpButtonGroup helpKey="order.returns-detail.action.allocate">
                          <Button variant="default" onClick={() => onAllocate?.(it)}>
                            {it.inventoryImpact ? t("itemsTable.edit") : t("itemsTable.allocate")}
                          </Button>
                        </HelpButtonGroup>
                      </ReasonTooltip>
                    )
                  ) : null}
                </Group>
              </Group>
            );
          })}
        </Stack>
      </Paper>

      {/* ── 4. Quantity history (D15) ── */}
      <Paper withBorder radius="md" p="md">
        <Title order={6} mb="sm">{t("itemsTable.qtyHistoryTitle")}</Title>
        <Stack gap="sm">
          {items.map((it) => {
            const received = it.receivedQuantity;
            const partial = received != null && received < it.quantity;
            return (
              <Group key={it.id} justify="space-between" align="center" wrap="wrap" gap="md">
                <Text size="sm" fw={500} flex={1}>{it.productName}</Text>
                <Group gap="xl" wrap="wrap">
                  <QtyStat label={t("itemsTable.requested")} value={it.quantity} />
                  <QtyStat label={t("itemsTable.approved")} value="-" />
                  <QtyStat
                    label={t("itemsTable.received")}
                    value={received != null ? (partial ? t("itemsTable.receivedPartial", { count: received }) : `${received}`) : "-"}
                    color={partial ? "red" : undefined}
                  />
                </Group>
              </Group>
            );
          })}
        </Stack>
      </Paper>

      {/* Zoomable photo gallery (shared by table + gallery section) */}
      <Modal opened={galleryOpened} onClose={galleryH.close} size="lg" centered
        title={<Group gap="xs"><ImageIcon size={18} /><Text fw={600}>{t("itemsTable.photoEvidence")}</Text></Group>}>
        {gallery.length ? (
          <Stack gap="sm">
            <Image src={gallery[gIdx]} radius="md" mah={440} fit="contain" fallbackSrc="https://placehold.co/600x400?text=No+image" />
            {gallery.length > 1 ? (
              <Group justify="center" gap="md">
                <ActionIcon variant="default" onClick={gPrev} aria-label={t("transitResolve.prev")}><CaretLeftIcon size={18} /></ActionIcon>
                <Text size="sm" style={TNUM}>{gIdx + 1} / {gallery.length}</Text>
                <ActionIcon variant="default" onClick={gNext} aria-label={t("transitResolve.next")}><CaretRightIcon size={18} /></ActionIcon>
              </Group>
            ) : null}
            {gallery.length > 1 ? (
              <Group gap="xs" wrap="wrap" justify="center">
                {gallery.map((p, i) => (
                  <UnstyledButton key={i} onClick={() => setGIdx(i)}>
                    <Image src={p} w={52} h={52} radius="sm" fit="cover" fallbackSrc="https://placehold.co/72?text=?" />
                  </UnstyledButton>
                ))}
              </Group>
            ) : null}
          </Stack>
        ) : null}
      </Modal>
    </Stack>
  );
}
